/**
 * Forecast Detail — the records behind one clicked grid cell.
 *
 * Shared by Forecast cells (pure POForecast rows have no PO) and Open PO
 * cells (PO Number / Line are real). CR-5309: PO Number / Line render only
 * when at least one detail row carries a PO number, so the empty columns
 * vanish for forecasts while Open PO and PO-past-firm-boundary
 * (po_line_forecast) rows keep their references.
 */
import { useMemo } from 'react';
import PoLineLink from '../../../common/PoLineLink';
import PoHeaderLink from '../../../common/PoHeaderLink';
import { useFormatters } from '../../../../contexts/PreferencesContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../ui/dialog';
import { Loader2 } from 'lucide-react';

interface ForecastDrilldownDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  loading: boolean;
  rows: Record<string, unknown>[];
}

const TYPE_BADGE: Record<string, { label: string; className: string }> = {
  forecast: { label: 'Forecast', className: 'bg-blue-100 text-blue-800' },
  po_line_forecast: { label: 'PO (Forecast)', className: 'bg-purple-100 text-purple-800' },
};
const OPEN_PO_BADGE = { label: 'Open PO', className: 'bg-green-100 text-green-800' };

export default function ForecastDrilldownDialog({
  open, onOpenChange, title, loading, rows,
}: ForecastDrilldownDialogProps) {
  const { formatDate } = useFormatters();
  const showPoColumns = useMemo(
    () => rows.some((item) => {
      const po = item['po_number'];
      return po !== undefined && po !== null && String(po).trim() !== '';
    }),
    [rows],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Forecast Detail</DialogTitle>
          <DialogDescription>{title}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No detail records found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-3 py-2 text-left font-medium">Type</th>
                  {showPoColumns && (
                    <>
                      <th className="px-3 py-2 text-left font-medium">PO Number</th>
                      <th className="px-3 py-2 text-left font-medium">Line</th>
                    </>
                  )}
                  <th className="px-3 py-2 text-left font-medium">Part Number</th>
                  <th className="px-3 py-2 text-left font-medium">Description</th>
                  <th className="px-3 py-2 text-left font-medium">Date</th>
                  <th className="px-3 py-2 text-right font-medium">Quantity</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item, idx) => {
                  const badge = TYPE_BADGE[String(item['type'])] ?? OPEN_PO_BADGE;
                  const date = item['forecast_date'] || item['delivery_date'];
                  return (
                    <tr key={idx} className="border-b hover:bg-muted/30">
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.className}`}>
                          {badge.label}
                        </span>
                      </td>
                      {showPoColumns && (
                        <>
                          {/* CR-1154: PO number links to the PO header; line to the line item. */}
                          <td className="px-3 py-2 font-mono whitespace-nowrap">
                            <PoHeaderLink poNumber={item['po_number'] as string | undefined}>
                              {String(item['po_number'] || '-')}
                            </PoHeaderLink>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <PoLineLink poLineId={item['po_line_id'] as number | undefined}>
                              {String(item['line_number'] || '-')}
                            </PoLineLink>
                          </td>
                        </>
                      )}
                      <td className="px-3 py-2 whitespace-nowrap">{String(item['part_number'] || '')}</td>
                      <td className="px-3 py-2 text-muted-foreground text-xs">{String(item['description'] || '')}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{date ? formatDate(String(date)) : ''}</td>
                      <td className="px-3 py-2 text-right font-mono font-medium">
                        {parseFloat(String(item['qty_outstanding'] || item['qty_forecast'] || 0)).toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
